import { Optional } from 'ts-toolbelt/out/Object/Optional';
import { useNavigate, useMatch, type AnySearch } from '@frontend/weave-router';
import { ThreadCreationRoute, InboxThreadRoute, InboxType } from './types';

type BaseNavigateArgs = {
  replace?: boolean;
};

export type InboxThreadNavigateArgs = BaseNavigateArgs & Optional<InboxThreadRoute, 'filter'>;

export type InboxThreadCreationNavigateArgs = BaseNavigateArgs & Optional<ThreadCreationRoute, 'filter'>;

export type CloseThreadNavigateArgs = BaseNavigateArgs & {
  filter?: InboxType;
};

const convertStringToInboxType = (val: string): InboxType => {
  switch (val) {
    case InboxType.ARCHIVED:
      return InboxType.ARCHIVED;
    case InboxType.BLOCKED:
      return InboxType.BLOCKED;
    case InboxType.SCHEDULED:
      return InboxType.SCHEDULED;
    case InboxType.DRAFTS:
      return InboxType.DRAFTS;
    default:
      return InboxType.INBOX;
  }
};

export const useInboxNavigate = () => {
  const navigate = useNavigate();
  const parentMatch = useMatch({ from: '/messages/$filter', shouldThrow: false });
  const threadMatch = useMatch({ from: '/messages/$filter/$groupId_/$threadId', shouldThrow: false });
  const threadSearch = threadMatch?.search;
  const threadParams = threadMatch?.params;
  const currentParams = parentMatch?.params ?? threadParams;
  const defaultFilter = convertStringToInboxType(currentParams?.filter ?? InboxType.INBOX);

  const navigateToThread = ({ groupId, threadId, filter, replace, ...search }: InboxThreadNavigateArgs) => {
    let resolvedClickCount = search.click;
    if (search.smsId) {
      if (search.smsId === threadSearch?.smsId) resolvedClickCount = (threadSearch.click ?? 0) + 1;
      else resolvedClickCount = 1;
    }
    navigate({
      to: '/messages/$filter/$groupId/$threadId',
      params: {
        filter: defaultFilter || 'inbox',
        groupId,
        threadId,
      },
      search: {
        ...search,
        click: resolvedClickCount,
      },
      replace,
    });
  };

  const navigateToThreadCreation = ({
    filter,
    replace,
    ...search
  }: InboxThreadCreationNavigateArgs | undefined = {}) => {
    navigate({
      to: '/messages/$filter/new',
      params: { filter: defaultFilter || 'inbox' },
      search: { ...search },
      replace,
    });
  };

  const closeThread = ({ filter, replace }: CloseThreadNavigateArgs = {}) => {
    navigate({ to: '/messages/$filter', params: { filter: filter || defaultFilter || 'inbox' }, replace });
  };

  /**
   * Intended to be used when the user has an existing thread open.
   */
  const updatePersonId = (personId: string) => {
    const { personId: _, ...newSearch } = threadSearch || {};
    if (personId) {
      navigate({ search: { ...newSearch, personId } as AnySearch });
      return;
    }
    navigate({ search: newSearch } as AnySearch);
  };

  const setIsNew = (isNew: boolean) => {
    if (!threadParams?.groupId || !threadParams?.threadId) {
      console.error('Cannot update personId without groupId and threadId');
      return;
    }
    navigate({
      to: '/messages/$filter/$groupId/$threadId',
      params: {
        filter: threadParams?.filter || defaultFilter || 'inbox',
        groupId: threadParams?.groupId,
        threadId: threadParams?.threadId,
      },
      search: { ...threadSearch, isNew },
    });
  };

  const setIsArchived = (isArchived: boolean) => {
    if (!threadParams?.groupId || !threadParams?.threadId) {
      console.error('Cannot update personId without groupId and threadId');
      return;
    }
    navigate({
      to: '/messages/$filter/$groupId/$threadId',
      params: {
        filter: threadParams?.filter || defaultFilter || 'inbox',
        groupId: threadParams?.groupId,
        threadId: threadParams?.threadId,
      },
      search: { ...threadSearch, isArchived },
    });
  };

  return {
    navigateToThread,
    navigateToThreadCreation,
    closeThread,
    updatePersonId,
    setIsNew,
    setIsArchived,
  };
};
